import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './serengeti.css';
import { IMAGE_FALLBACK, SERENGETI_HERO_SLIDES, SERENGETI_PAGE_IMAGES } from '../../data/destinationImages';

import serengetiSign from '../../assets/images/serengeti-sign.jpg';
import elephantFace from '../../assets/images/elephant-face.jpg';
import wildebeestFace from '../../assets/images/wildebeest-face.jpg';
import rhinoFace from '../../assets/images/rhino-face.jpg';
import wilddogFace from '../../assets/images/wilddog-face.jpg';
import eagleFace from '../../assets/images/eagle-face.jpg';

const SERENGETI_IMAGES = SERENGETI_PAGE_IMAGES;

/* ─── Page data ─── */
const HERO_STATS = [
  { value: '14,763', label: 'km² of wilderness', delay: 0 },
  { value: '1.5M+', label: 'Wildebeest migrate', delay: 80 },
  { value: '3,000+', label: 'Lions in the park', delay: 160 },
  { value: 'UNESCO', label: 'World Heritage Site', delay: 240 },
];

const WILDLIFE = [
  {
    animalFace: serengetiSign,
    title: 'Big Cats',
    text: 'Home to one of Africa highest concentrations of lions, cheetahs, and leopards. Serengeti is predator country.',
    facePosition: 'center 45%',
  },
  {
    animalFace: elephantFace,
    title: 'Elephant Herds',
    text: 'Large family groups roam the western corridor and Grumeti River area year-round.',
    facePosition: 'center 42%',
  },
  {
    animalFace: wildebeestFace,
    title: 'Great Migration',
    text: 'Over 1.5 million wildebeest and 250,000 zebras traverse the plains in the world most spectacular wildlife event.',
    facePosition: 'center 35%',
  },
  {
    animalFace: rhinoFace,
    title: 'Black Rhinos',
    text: 'Small but vital populations inhabit the Moru Kopjes area one of Tanzanias best Rhino spotting zones.',
    facePosition: 'center 40%',
  },
  {
    animalFace: wilddogFace,
    title: 'Wild Dogs',
    text: 'Endangered African wild dogs make rare appearances in remote areas of the northern and eastern Serengeti.',
    facePosition: 'center 36%',
  },
  {
    animalFace: eagleFace,
    title: 'Birdlife',
    text: 'Over 500 bird species from martial eagles and secretary birds to vibrant lilac-breasted rollers.',
    facePosition: 'center 28%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Peak game viewing as animals gather around remaining water sources. The Great Migration river crossings happen in the north.',
  },
  {
    badge: 'green',
    title: 'Green Season',
    months: 'November – March',
    text: 'Calving season on the southern plains. Lush landscapes, fewer crowds, and dramatic thunderstorm skies.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'April – May',
    text: 'Long rains bring lower rates and quieter camps. Some roads are challenging, but wildlife is still abundant.',
  },
];

const EXPERIENCES = [
  {
    image: SERENGETI_IMAGES.balloon,
    title: 'Hot Air Balloon Safari',
    text: 'Float above the Serengeti at dawn for a bird-eye view of the plains, followed by a champagne bush breakfast.',
  },
  {
    image: SERENGETI_IMAGES.wildlife,
    title: 'Classic Game Drives',
    text: 'Open-vehicle game drives with expert guides, dawn, midday, and sunset drives across the ecosystem.',
  },
  {
    image: SERENGETI_IMAGES.migration,
    title: 'Migration Tracking',
    text: 'Follow the Great Migration through the Serengetis zones from calving grounds to river crossings.',
  },
];

const GALLERY_IMAGES = [
  { src: SERENGETI_IMAGES.hero, caption: 'Serengeti Plains' },
  { src: SERENGETI_IMAGES.wildlife, caption: 'Serengeti Predators' },
  { src: SERENGETI_IMAGES.migration, caption: 'Great Migration' },
  { src: SERENGETI_IMAGES.balloon, caption: 'Balloon Safari' },
  { src: SERENGETI_IMAGES.overview, caption: 'Golden Savanna' },
  { src: SERENGETI_IMAGES.savanna, caption: 'Zebra Herds' },
];

const SAFARI_PACKAGES = [
  {
    image: SERENGETI_IMAGES.wildlife,
    title: 'Serengeti Fly-In Escape',
    duration: '3–4 days',
    tag: 'Short safari',
    text: 'A focused Serengeti immersion fly into Seronera, settle into camp, and spend full days on game drives across the central plains.',
    highlights: ['Return flights from Arusha', 'Private game drives', 'Central Serengeti camps'],
  },
  {
    image: SERENGETI_IMAGES.balloon,
    title: 'Migration & Balloon Safari',
    duration: '5–7 days',
    tag: 'Peak season',
    text: 'Time your visit for the Great Migration and add a dawn hot-air balloon flight over the herds for the ultimate Serengeti highlight.',
    highlights: ['Migration tracking', 'Balloon safari', 'Bush breakfast'],
  },
  {
    image: SERENGETI_IMAGES.savanna,
    title: 'Luxury Serengeti Retreat',
    duration: '4–6 days',
    tag: 'Premium',
    text: 'Stay in hand-picked luxury tented camps with private guides, flexible schedules, and the option to add walking safaris or sundowners.',
    highlights: ['Luxury camps', 'Private vehicle', 'Flexible daily plan'],
  },
  {
    image: SERENGETI_IMAGES.migration,
    title: 'Classic Northern Circuit',
    duration: '6–8 days',
    tag: 'Most popular',
    text: 'Combine Tarangire, Ngorongoro Crater, and the Serengeti for the complete northern Tanzania safari with varied landscapes and wildlife.',
    highlights: ['Multi-park itinerary', 'Crater descent', 'Migration window options'],
  },
];

const GETTING_THERE_ROUTES = [
  'Fly from Arusha or Kilimanjaro International Airport to Seronera airstrip (approx. 1.5 hrs)',
  'Drive from Arusha via Ngorongoro Conservation Area gate (approx. 7–8 hrs)',
  'Charter flights available to remote airstrips in the northern and western corridors',
  'Internal transfers from other Northern Circuit parks (Tarangire, Lake Manyara)',
];

/* ─── Lightbox component ─── */
function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="serengeti-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="serengeti-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="serengeti-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="serengeti-lightbox__caption">{image.caption}</span>
    </div>
  );
}

/* ─── Main page component ─── */
function Serengeti() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="serengeti">
      {/* ──── Hero ──── */}
      <section className="serengeti-hero" aria-labelledby="serengeti-hero-heading">
        <div className="serengeti-hero__media" aria-hidden="true">
          <HeroSlideshow classPrefix="serengeti-hero" slides={SERENGETI_HERO_SLIDES} fallback={IMAGE_FALLBACK} />
          <div className="serengeti-hero__overlay" />
        </div>
        <div className="page-container serengeti-hero__container">
          <div className="serengeti-hero__content">
            <nav className="serengeti-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="serengeti-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#safaris">Northern Circuit</a>
              <span className="serengeti-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Serengeti</span>
            </nav>
            <p className="serengeti-hero__eyebrow">Northern Safari Circuit</p>
            <h1 id="serengeti-hero-heading">
              Serengeti
              <span>National Park</span>
            </h1>
            <p className="serengeti-hero__lead">
              Endless plains, the Great Migration, and Africa&apos;s most iconic wildlife
              , Serengeti is where the classic safari dream comes alive.
            </p>
            <div className="serengeti-hero__actions">
              <a className="serengeti-btn serengeti-btn--primary" href="#contact">
                Plan your Serengeti safari
              </a>
              <a className="serengeti-btn serengeti-btn--outline" href="#serengeti-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="serengeti-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="serengeti-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="serengeti-hero__stat-value">{stat.value}</span>
              <span className="serengeti-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ──── Overview ──── */}
      <section
        id="serengeti-overview"
        className="serengeti-section serengeti-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="serengeti-overview__grid">
            <Reveal className="serengeti-overview__text">
              <p className="serengeti-section__eyebrow">About the park</p>
              <h2 id="overview-heading">Where Africa&apos;s wilderness begins</h2>
              <p>
                The Serengeti National Park is Tanzania&apos;s oldest and most popular
                national park, spanning 14,763 square kilometres of grassland plains,
                savanna, riverine forest, and woodland hills. Named from the Maasai word
                &ldquo;Siringet&rdquo; meaning &ldquo;endless plains,&rdquo; the
                landscape lives up to its name.
              </p>
              <p>
                Beyond the Great Migration, the Serengeti hosts one of the most complex
                and diverse ecosystems on Earth supporting over 70 large mammal
                species and 500 bird species. From the dramatic river crossings in the
                north to the calving season on the southern plains, there is always
                something extraordinary happening.
              </p>
              <ul className="serengeti-overview__details">
                <Reveal as="li" className="serengeti-overview__detail" delay={0}>
                  <span className="serengeti-overview__detail-label">Location</span>
                  <span className="serengeti-overview__detail-value">Northern Tanzania</span>
                </Reveal>
                <Reveal as="li" className="serengeti-overview__detail" delay={80}>
                  <span className="serengeti-overview__detail-label">Size</span>
                  <span className="serengeti-overview__detail-value">14,763 km²</span>
                </Reveal>
                <Reveal as="li" className="serengeti-overview__detail" delay={160}>
                  <span className="serengeti-overview__detail-label">Established</span>
                  <span className="serengeti-overview__detail-value">1951</span>
                </Reveal>
                <Reveal as="li" className="serengeti-overview__detail" delay={240}>
                  <span className="serengeti-overview__detail-label">Best for</span>
                  <span className="serengeti-overview__detail-value">Big 5 &amp; Migration</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="serengeti-overview__image" variant="right" delay={200}>
              <SafeImage
                className="serengeti-overview__img"
                src={SERENGETI_IMAGES.overview}
                alt="Serengeti National Park entrance sign on the savanna"
              />
              <span className="serengeti-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──── Safari packages ──── */}
      <section
        id="serengeti-packages"
        className="serengeti-section serengeti-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="serengeti-section__header">
            <p className="serengeti-section__eyebrow">Safari packages</p>
            <h2 id="packages-heading">Serengeti safari packages</h2>
            <p className="serengeti-section__intro">
              Ready-to-customise itineraries from a short fly-in escape to a full
              Northern Circuit journey every package is tailored to your dates and style.
            </p>
          </Reveal>
          <ul className="serengeti-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="serengeti-packages__card"
                delay={index * 100}
              >
                <div className="serengeti-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="serengeti-packages__duration">{pkg.duration}</span>
                </div>
                <div className="serengeti-packages__card-body">
                  <span className="serengeti-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="serengeti-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a
                    className="serengeti-packages__cta"
                    href="#contact"
                  >
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ──── Wildlife highlights ──── */}
      <section
        id="serengeti-wildlife"
        className="serengeti-section serengeti-wildlife"
        aria-labelledby="wildlife-heading"
      >
        <div className="page-container">
          <Reveal className="serengeti-section__header">
            <p className="serengeti-section__eyebrow">Iconic wildlife</p>
            <h2 id="wildlife-heading">The animals of the Serengeti</h2>
            <p className="serengeti-section__intro">
              The Serengeti ecosystem is one of the planet&apos;s most diverse, a living
              stage for predators, prey, and the greatest wildlife migration on Earth.
            </p>
          </Reveal>
          <ul className="serengeti-wildlife__grid">
            {WILDLIFE.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="serengeti-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="serengeti-wildlife__icon-frame">
                  <span className="serengeti-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="serengeti-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.animalFace} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ──── Gallery ──── */}
      <section
        id="serengeti-gallery"
        className="serengeti-section serengeti-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="serengeti-section__header">
            <p className="serengeti-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from the Serengeti</h2>
            <p className="serengeti-section__intro">
              From golden-hour landscapes to up-close wildlife encounters
              , a glimpse into the Serengeti experience.
            </p>
          </Reveal>
          <div className="serengeti-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="serengeti-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="serengeti-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      {/* ──── Best time to visit ──── */}
      <section
        id="serengeti-seasons"
        className="serengeti-section serengeti-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="serengeti-section__header">
            <p className="serengeti-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit the Serengeti</h2>
            <p className="serengeti-section__intro">
              The Serengeti is a year-round destination but each season offers a
              different spectacle. Here&apos;s how to choose your window.
            </p>
          </Reveal>
          <ul className="serengeti-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="serengeti-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`serengeti-seasons__card-badge serengeti-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="serengeti-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ──── Experiences ──── */}
      <section
        id="serengeti-experiences"
        className="serengeti-section serengeti-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="serengeti-section__header">
            <p className="serengeti-section__eyebrow">Safari experiences</p>
            <h2 id="experiences-heading">Ways to explore the Serengeti</h2>
            <p className="serengeti-section__intro">
              From dawn game drives to sunrise balloon flights every Serengeti
              experience is designed to bring you closer to the wild.
            </p>
          </Reveal>
          <ul className="serengeti-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="serengeti-experiences__card"
                delay={index * 120}
              >
                <div className="serengeti-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="serengeti-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ──── Getting there ──── */}
      <section
        id="serengeti-getting-there"
        className="serengeti-section serengeti-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="serengeti-getting-there__inner">
            <Reveal className="serengeti-getting-there__copy">
              <p className="serengeti-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach the Serengeti</h2>
              <p>
                The Serengeti is in northern Tanzania, roughly 335 km northwest of
                Arusha. Most visitors arrive by air or drive through the Ngorongoro
                Conservation Area both options offer unforgettable experiences on
                their own.
              </p>
              <ul className="serengeti-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="serengeti-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Serengeti National Park location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d510078.2397730944!2d34.30!3d-2.33!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x18321c8b3cb8d057%3A0x7f37e1f0b9b5f4c!2sSerengeti%20National%20Park!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──── CTA ──── */}
      <section className="serengeti-cta" aria-labelledby="serengeti-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="serengeti-cta__inner">
              <h2 id="serengeti-cta-heading">
                Ready for your Serengeti adventure?
              </h2>
              <p>
                Tell us your travel dates and preferences we&apos;ll design a
                tailor-made Serengeti safari itinerary within 24 hours.
              </p>
              <a
                className="serengeti-btn serengeti-btn--primary serengeti-btn--lg"
                href="#contact"
              >
                Get a free Serengeti quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Serengeti;
